// AI Drill Service for Primary English
// Generates similar practice questions using DeepSeek API

class AiDrillService {
  constructor() {
    this.apiKey = 'sk-7549b29d010a424a836d081a478552f4';
    this.apiUrl = 'https://api.deepseek.com/chat/completions';
  }

  // Calculate cost based on DeepSeek API tariff
  calculateCost(inputTokens, outputTokens, isStandardTime = true) {
    const inputCostPerMillion = isStandardTime ? 2 : 1; // Standard: 2元, Discount: 1元
    const outputCostPerMillion = isStandardTime ? 8 : 4; // Standard: 8元, Discount: 4元
    
    const inputCost = (inputTokens / 1000000) * inputCostPerMillion;
    const outputCost = (outputTokens / 1000000) * outputCostPerMillion;
    
    return {
      inputCost: inputCost,
      outputCost: outputCost,
      totalCost: inputCost + outputCost,
      currency: 'CNY'
    };
  }

  // Check if current time is in standard period (08:30-00:30 Beijing time)
  isStandardTime() {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getUTCHours();
    return hour >= 8.5 && hour < 24.5; // 08:30-00:30
  }

  // Generate drill questions based on original question
  async generateDrillQuestions(originalQuestion, topic, grade) {
    const startTime = Date.now();
    const isStandardTime = this.isStandardTime();
    
    try {
      const prompt = `Generate 5 similar practice questions for Primary English learning.

ORIGINAL QUESTION:
${originalQuestion.question}
Answer: ${originalQuestion.answer}
Explanation: ${originalQuestion.explanation}

TOPIC: ${topic.title}
GRADE: ${grade}
DIFFICULTY: Same as original question

REQUIREMENTS:
1. Create 5 similar questions with the same question type and difficulty level
2. Use similar vocabulary and sentence patterns
3. Vary the numbers, names, or specific details while keeping the same structure
4. Each question should have 4 multiple choice options (A, B, C, D)
5. Only one option should be correct
6. Include explanations for each question

IMPORTANT: Respond ONLY with valid JSON in this exact format:
{
  "drillQuestions": [
    {
      "id": 1,
      "question": "Complete the sentence: \"I _____ a teacher.\"",
      "options": [
        {"letter": "A", "text": "am", "isCorrect": true},
        {"letter": "B", "text": "is", "isCorrect": false},
        {"letter": "C", "text": "are", "isCorrect": false},
        {"letter": "D", "text": "be", "isCorrect": false}
      ],
      "correctAnswer": "A",
      "explanation": "Use \"am\" with the subject \"I\" in present tense."
    }
  ],
  "metadata": {
    "originalQuestion": "${originalQuestion.question}",
    "topic": "${topic.title}",
    "grade": "${grade}",
    "generatedAt": "${new Date().toISOString()}"
  }
}

CRITICAL: You must respond with ONLY valid JSON. Do not include any explanatory text, markdown formatting, or additional comments. The response must start with { and end with }. Only the JSON object is allowed.`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Calculate cost
      const cost = this.calculateCost(
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0,
        isStandardTime
      );
      
      // Log timing and token information
      console.log(`AI Drill Generation - Elapsed Time: ${elapsedTime}ms`);
      console.log(`Tokens Used - Input: ${data.usage?.prompt_tokens || 0}, Output: ${data.usage?.completion_tokens || 0}, Total: ${data.usage?.total_tokens || 0}`);
      console.log(`Cost: ${cost.totalCost.toFixed(4)} CNY`);
      
      // Extract JSON from response
      const parsedResult = this.extractAndParseJSON(content);
      if (parsedResult) {
        return {
          ...parsedResult,
          metadata: {
            ...parsedResult.metadata,
            elapsedTime,
            tokensUsed: data.usage?.total_tokens || 0,
            cost
          }
        };
      } else {
        console.warn('Failed to extract valid JSON from drill response, using fallback');
        return this.createFallbackDrillQuestions(originalQuestion, topic, grade, elapsedTime, data.usage, cost);
      }
    } catch (error) {
      console.error('AI Drill API Error:', error);
      return this.createFallbackDrillQuestions(originalQuestion, topic, grade, 0, { total_tokens: 0 }, { totalCost: 0, currency: 'CNY' });
    }
  }

  // Robust JSON extraction method
  extractAndParseJSON(content) {
    if (!content || typeof content !== 'string') {
      return null;
    }

    // Method 1: Try direct JSON parsing first
    try {
      const parsed = JSON.parse(content.trim());
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (e) {
      // Continue to other methods
    }

    // Method 2: Look for JSON blocks in markdown code blocks
    const codeBlockMatch = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (codeBlockMatch) {
      try {
        const parsed = JSON.parse(codeBlockMatch[1]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 3: Look for JSON between curly braces
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    return null;
  }

  // Create fallback drill questions when API fails
  createFallbackDrillQuestions(originalQuestion, topic, grade, elapsedTime, usage, cost) {
    const drillQuestions = [];
    
    // Generate 5 similar questions based on the original
    for (let i = 1; i <= 5; i++) {
      const questionVariations = [
        {
          question: `Complete the sentence: "I _____ a student."`,
          options: [
            { letter: "A", text: "am", isCorrect: true },
            { letter: "B", text: "is", isCorrect: false },
            { letter: "C", text: "are", isCorrect: false },
            { letter: "D", text: "be", isCorrect: false }
          ],
          correctAnswer: "A",
          explanation: "Use \"am\" with the subject \"I\" in present tense."
        },
        {
          question: `Choose the correct form: "She _____ happy."`,
          options: [
            { letter: "A", text: "am", isCorrect: false },
            { letter: "B", text: "is", isCorrect: true },
            { letter: "C", text: "are", isCorrect: false },
            { letter: "D", text: "be", isCorrect: false }
          ],
          correctAnswer: "B",
          explanation: "Use \"is\" with singular subjects like \"he\", \"she\", \"it\"."
        },
        {
          question: `Fill in the blank: "They _____ friends."`,
          options: [
            { letter: "A", text: "am", isCorrect: false },
            { letter: "B", text: "is", isCorrect: false },
            { letter: "C", text: "are", isCorrect: true },
            { letter: "D", text: "be", isCorrect: false }
          ],
          correctAnswer: "C",
          explanation: "Use \"are\" with plural subjects like \"we\", \"you\", \"they\"."
        },
        {
          question: `Complete: "We _____ students."`,
          options: [
            { letter: "A", text: "am", isCorrect: false },
            { letter: "B", text: "is", isCorrect: false },
            { letter: "C", text: "are", isCorrect: true },
            { letter: "D", text: "be", isCorrect: false }
          ],
          correctAnswer: "C",
          explanation: "Use \"are\" with \"we\" (first person plural)."
        },
        {
          question: `Choose the correct form: "It _____ a cat."`,
          options: [
            { letter: "A", text: "am", isCorrect: false },
            { letter: "B", text: "is", isCorrect: true },
            { letter: "C", text: "are", isCorrect: false },
            { letter: "D", text: "be", isCorrect: false }
          ],
          correctAnswer: "B",
          explanation: "Use \"is\" with \"it\" (singular third person)."
        }
      ];

      drillQuestions.push({
        id: i,
        ...questionVariations[i - 1]
      });
    }

    return {
      drillQuestions,
      metadata: {
        originalQuestion: originalQuestion.question,
        topic: topic.title,
        grade: grade,
        generatedAt: new Date().toISOString(),
        elapsedTime,
        tokensUsed: usage?.total_tokens || 0,
        cost
      }
    };
  }
}

export default new AiDrillService(); 